/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import React, { Fragment, useState } from 'react'
import { useDebouncedCallback } from 'utils/hooks/useDebounce'
import { Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import TablePageSelectorItemsList from './TablePageSelectorComponents/TablePageSelectorItemsList'
import usePageArray from './TablePageSelectorComponents/usePageArray'

interface TablePageSelectorProps {
    pageCount: number
    selected?: number
    onChange: (page: number) => void
    disabled?: boolean
}

const TablePageSelector = (props: TablePageSelectorProps) => {
    const resolvedSelected = Number(props.selected ?? 0)
    const [internalValue, setInternalValue] = useState<number>(resolvedSelected)
    const [prevSelected, setPrevSelected] = useState(props.selected)
    const pageArray = usePageArray(props.pageCount)
    const debouncedChangeHandler = useDebouncedCallback(
        (data) => props.onChange(data),
        300
    )

    if (props.selected !== prevSelected) {
        setPrevSelected(props.selected)
        setInternalValue(resolvedSelected)
    }

    const onChangeHandler = (newPage) => {
        const newPageNumber = Number(newPage)
        setInternalValue(Number(newPageNumber))
        debouncedChangeHandler(Number(newPageNumber) - 1)
    }

    return (
        <div className={clsx('TablePageSelector relative min-w-12.5 z-10 mr-1.5')}>
            <Listbox
                disabled={props.disabled}
                value={internalValue}
                onChange={onChangeHandler}
            >
                {({ open }) => (
                    <div className={clsx('group rounded-lg')}>
                        <Listbox.Button
                            className={clsx(
                                'TableFoot-control',
                                'ui-open:text-blue-500 relative w-full cursor-pointer rounded-lg justify-between px-3 transition-all duration-200 border shadow-sm',
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-md',
                                'dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
                                open &&
                                    'ring-2 ring-blue-500/20 border-blue-500 shadow-md'
                            )}
                        >
                            <span
                                className={clsx(
                                    'block truncate pr-6 text-left'
                                )}
                            >
                                {pageArray.find(
                                    (item) => item === internalValue
                                )}
                            </span>
                            <span
                                className={clsx(
                                    'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 group-hover:text-blue-500',
                                    open && 'text-blue-500'
                                )}
                            >
                                <ChevronUpDownIcon
                                    className={clsx('h-4 w-4')}
                                />
                            </span>
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={clsx(
                                    'absolute bottom-full mb-1 z-50 w-full overflow-auto overscroll-contain rounded-lg bg-white py-1 text-base sm:text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none max-h-[min(15rem,calc(100dvh-8rem))] dark:bg-gray-950 dark:ring-gray-800'
                                )}
                            >
                                <TablePageSelectorItemsList
                                    items={pageArray}
                                />
                            </Listbox.Options>
                        </Transition>
                    </div>
                )}
            </Listbox>
        </div>
    )
}

export default TablePageSelector
